import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { browserName, deviceDetect, isDesktop } from 'react-device-detect';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';

import { DEMO_LOGINS, ELoginAs, ELoginSource } from '@aqx-core/common/api/auth';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { useMarketingPageUrl } from '@aqx-core/common/hooks/use-marketing-page-url';
import { getQueryLangFromTLang } from '@aqx-core/common/i18n/utils';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { ILoginFormParams } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { EUISnackBarMessageType } from '@aqx-core/common/services/snackbar';
import { getMarketingPageUrl } from '@aqx-member/web/utils/marketing-page-url';

interface IUseLoginFormProps {
  selectedTab: ELoginAs | undefined;
}

export const useLoginForm = ({ selectedTab }: IUseLoginFormProps) => {
  const { t } = useTranslation();
  const isDemo = selectedTab ? DEMO_LOGINS.includes(selectedTab) : false;

  useMarketingPageUrl();

  const schema = useMemo(() => {
    const userIdLabel = selectedTab === ELoginAs.LIVE ? t('email / username') : t('account ID');

    return yup.object().shape({
      userId: yup
        .string()
        .required(() => t('Please enter a valid {{label}}.', { label: userIdLabel }))
        .matches(/^[^\s]+$/i, () => t('Please enter a valid {{label}}.', { label: userIdLabel })),
      password: yup.string().required(() => t('Please enter a valid password.'))
    });
  }, [selectedTab, t]);

  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const useFormMethods = useBaseForm<ILoginFormParams>({
    resolver,
    mode: 'onBlur',
    shouldFocusError: false
  });

  const { handleSubmit, trigger } = useFormMethods;

  const handleError = useCallback((code: ECommonResponseCode, message: string) => {
    showBasicSnackBar({
      message: mapResponseCodeToMessage(code as ECommonResponseCode, message),
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  /**
   * Currently checks for web version only.
   * TODO: Implement native mobile detection in the future.
   */
  const handleFormSubmit: SubmitHandler<ILoginFormParams> = useCallback(
    (values) => {
      const marketingPageUrl = isDemo ? getMarketingPageUrl() : undefined;
      const language = isDemo ? getQueryLangFromTLang() : undefined;
      authenticationViewController.login({
        ...values,
        source: isDesktop ? ELoginSource.WEB : ELoginSource.MOBILE_WEB,
        browserType: browserName ?? undefined,
        deviceType: deviceDetect(window.navigator.userAgent)?.osName ?? undefined,
        marketingPageUrl,
        language,
        errorCallback: handleError
      });
    },
    [handleError, isDemo]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  return {
    useFormMethods,
    submit,
    trigger
  };
};
